import { EventEmitter, Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, Observable, Subject, Subscription, filter, firstValueFrom, map, takeUntil } from 'rxjs';
import { IMosaic } from '../components/mosaic/mosaic.component';
import { ISettingsModel } from '../pages/user/components/tile-toolbar/tile-toolbar.component';
import { Retryable, BackOffPolicy, ExponentialBackoffStrategy } from 'typescript-retry-decorator';
import { HubService } from './hub.service';
import { SessionStorageService } from './sessionStorage.service';
import { LocalStorageService } from './localStorage.service';
import { AuthService } from './auth.service';
import { HashTable } from '../../helper/hash-table';
import * as data from '../pages/user/components/layout-ddl/templates';
import { IMosaicNode, deepCopy } from '../components/mosaic/mosaic-node/mosaic-node.component';
import { UserModule } from '../pages/user/user.module';
import { AsyncService } from './async.service';
import { userApi } from './external/api/user';
import { hub } from './external/hub';
import { TimeFrame } from './external/config/tickers';


export interface IMosaicState {
    layout: IMosaic;
    isEdit: boolean;
}

export interface IPairState {
    isUserSelection: boolean;
    pairName: string;
}

export interface LayoutChange {
    name: string;
    tfs: Map<number, TimeFrame>;
}

@Injectable()
export class LayoutService implements OnDestroy {


    public static currentPairKey: string = "currentPair";
    public static currentlayoutKey: string = "currentLayoutName";
    public static autoSaveKey: string = "autoSave";

    private defaultNodeSettings: ISettingsModel = {
        currencyPair: "IM:EURUSD",
        updateTime: "30"
    };

    private currentLayout: IMosaic = {
        node: {
            settings: this.defaultNodeSettings
        }
    } as IMosaic;

    private cancelationToken: Subject<void>;
    private modifiedChangeEvent: Subject<boolean> = new Subject<boolean>();
    private layoutChangeEvent: BehaviorSubject<IMosaicState> = new BehaviorSubject<IMosaicState>(null);
    private layoutsColelctionEvent: Subject<string[]> = new Subject<string[]>();
    private pairChangeEvent: BehaviorSubject<IPairState> = new BehaviorSubject<IPairState>(null);
    private infoBoxEvent: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);
    private selectTimeFrameEvent = new Subject<TimeFrameToSelect[]>();
    public layoutChangeInfo: LayoutChange = {
        tfs: new Map<number, TimeFrame>(),
        name: null
    };

    private layouts: HashTable<IMosaic> = {};
    private modified: boolean = false;

    private editingLayout: IMosaic;

    constructor(private hubSrv: HubService,
        private localStorage: LocalStorageService,
        private sessionStorage: SessionStorageService,
        private auth: AuthService,
        private asyncSrv: AsyncService) {

        this.cancelationToken = asyncSrv.createCancelationToken();
        //  console.log("init LayoutService");
        this.saveSession(LayoutService.currentlayoutKey);
        this.saveSession(LayoutService.currentPairKey);
        this.initLayout();

    }
    ngOnDestroy(): void {
        // console.log("destroy LayoutService");
        this.asyncSrv.dispose(this.cancelationToken);
        this.hubSrv.layoutDestroy();
    }

    private saveSession = (key: string) => {
        let value = this.sessionStorage.getData(key) ||
            this.localStorage.getData(key);

        if (!value)
            return;

        this.sessionStorage.saveData(key, value);
        this.localStorage.saveData(key, value);
    }

    public CleanUp = () => {
        this.layoutChangeEvent.next(null);
    }

    private initLayout = () =>
        this.hubSrv.getUserSettingsChanged().pipe(takeUntil(this.cancelationToken)).subscribe(
            settings => {

                let layoutName = this.sessionStorage.getData(LayoutService.currentlayoutKey) ||
                    this.localStorage.getData(LayoutService.currentlayoutKey);

                if (settings.layouts == null || settings.layouts.length == 0) {
                    layoutName = 'default';
                    this.layouts[layoutName] = deepCopy(data.templates[0]);
                    this.layouts[layoutName].title = layoutName;
                }
                else {
                    for (const x of settings.layouts) {
                        const node = x.c as IMosaic;
                        if (!node) continue;

                        this.cleanLayout(node);
                        node.title = x.n;
                    }
                    this.layouts = Object.assign({}, ...settings.layouts.map(s => ({ [s.n]: s.c })));
                }

                //this.layouts = this.reSortLayouts(this.layouts);

                this.currentLayout = this.layouts[layoutName] ||
                    settings.layouts[0]?.c as IMosaic;

                let pair = this.getPair();
                //const ticker = hub.tickers.get(pair);
                //if (hub.tickers.size == 0 && !pair) {
                //    pair = null;
                //    this.savePairSelection(pair);
                //} else if (ticker == null) {
                //    pair = hub.tickers.values().next().value.id;
                //    this.savePairSelection(pair);
                //}


                this.layoutsColelctionEvent.next(this.getLayoutSortedNames());
                this.emitLayoutChange();

                this.pairChangeEvent.next({
                    pairName: pair,
                    isUserSelection: false
                });

                this.hubSrv.settingsChange();
            });

    public Arrange = () => {
        if (this.currentLayout?.node) {
            if (this.arrangeNode(this.currentLayout.node))
                this.invalidateLayout();
        }
    }
    private arrangeNode(node: IMosaicNode) {
        if (!node.nodes)
            return false;
        let r = false;
        for (let i = 0; i < node.nodes.length; i++)
            if (node.nodes[i].size != 50) {
                node.nodes[i].size = 50;
                r = true;
            }
        for (let i = 0; i < node.nodes.length; i++)
            if (this.arrangeNode(node.nodes[i]))
                r = true;
        return r;
    }
    public InitPair = () => {
        let pairName = this.getPair();

        this.pairChangeEvent.next({ pairName: pairName, isUserSelection: false });
    }

    private getPair = (): string => {
        let pair = this.sessionStorage.getData(LayoutService.currentPairKey) ||
            this.localStorage.getData(LayoutService.currentPairKey);
        if (pair == 'null')
            pair = null;
        return pair ?? 'BN:BTCUSDT';
    }
   

    //getEditModeChange = (): Observable<boolean> => this.editTogleEvent.asObservable();
    getLayoutChange = (): Observable<IMosaicState> => this.layoutChangeEvent.asObservable().pipe(filter(s => s != null));
    getModifiedChange = (): Observable<boolean> => this.modifiedChangeEvent.asObservable();
    getLayoutsChange = (): Observable<string[]> => this.layoutsColelctionEvent.asObservable();
    getPairChange = (): Observable<IPairState> => this.pairChangeEvent.asObservable().pipe(filter(s => s != null));
    getInfoBoxChange = (): Observable<boolean> => this.infoBoxEvent.asObservable();
    getTimeFrameToSelect = () => this.selectTimeFrameEvent.asObservable();

    setInfoBoxState = (state: boolean) => this.infoBoxEvent.next(state);

    getCurrentPair = () => this.getPair();

    cacheTimeFrames() {
        if (this.layoutChangeInfo.name != this.currentLayout.title) {
            this.layoutChangeInfo.name = this.currentLayout.title;
            this.layoutChangeInfo.tfs.clear();
        }
        if (this.layoutChangeInfo.tfs.size == 0)
            for (let i = 0; i < hub.TVs.length; i++)
                this.layoutChangeInfo.tfs.set(i, hub.TVs[i].getTimeFrameInfo());
        
    }
    restoreTimeFrames() {
        if (this.layoutChangeInfo.name != this.currentLayout.title)
            return;
        this.layoutChangeInfo.name = null;
        if (this.layoutChangeInfo.tfs.size == 0)
            return;
       const tfs: TimeFrameToSelect[] = [];
        for (const tf of this.layoutChangeInfo.tfs.keys())
            tfs.push({
                tvid: tf,
                tf: this.layoutChangeInfo.tfs.get(tf)
            });

        this.layoutChangeInfo.tfs.clear();
        this.selectTimeFrames(tfs);
    }

    selectTimeFrames(tfs: TimeFrameToSelect[]) {
        this.selectTimeFrameEvent.next(tfs);
    }

    private saveLayoutSelection = () => {
        if (this.currentLayout?.title == null)
            return;
        this.sessionStorage.saveData(LayoutService.currentlayoutKey, this.currentLayout.title);
        this.localStorage.saveData(LayoutService.currentlayoutKey, this.currentLayout.title);
    }

    private savePairSelection = (pair: string) => {
        this.sessionStorage.saveData(LayoutService.currentPairKey, pair);
        this.localStorage.saveData(LayoutService.currentPairKey, pair);
    }
    setCurrentPair = (pair: string) => {
        this.savePairSelection(pair);
        this.hubSrv.beginSetTicker();
        this.pairChangeEvent.next({ pairName: pair, isUserSelection: true });
        this.hubSrv.endSetTicker();
    }

    /**
     * Fix BehaviorSubject when layout change not from user perspective
     */
    emitNotUserPairChange = () => {
        this.pairChangeEvent.next({ pairName: this.getPair(), isUserSelection: false });
    }

    renameLayout = async (newName: string, oldName: string) => {
        if (this.checkLayoutTitle(newName))
            return false;

        let tmpLayout = this.layouts[oldName];
        let isCurrentLayout = this.currentLayout == tmpLayout;

        if (this.modified && isCurrentLayout)
            await this.flushChanges();

        let error: string = null;
        await userApi.renameLayout(oldName, newName).catch(err => {
            error = err;
        });
        if (error)
            return false;

        delete this.layouts[oldName];
        tmpLayout.title = newName;
        this.layouts[tmpLayout.title] = tmpLayout;
        //this.layouts = this.reSortLayouts(this.layouts);

        this.layoutsColelctionEvent.next(this.getLayoutSortedNames());
        if (!isCurrentLayout)
            return true;

        this.saveLayoutSelection();
        this.emitLayoutChange();
        return true;
    }

    //private reSortLayouts = (layouts: HashTable<IMosaic>): HashTable<IMosaic> =>
    //	Object.assign({}, ...Object.keys(layouts)
    //		.map((s) => ({
    //			title: s,
    //			order: layouts[s].order == null ? 0 : layouts[s].order,
    //			mosaic: layouts[s]
    //		}))
    //		.sort((n1, n2) => n1.order - n2.order)
    //		.map(s => ({ [s.title]: s.mosaic })));


    private setModified(modified: boolean): boolean {
        if (this.modified == modified)
            return false;
        this.modified = modified;
        this.modifiedChangeEvent.next(this.modified);
        return true;
    }

    private saveLayout = async (layout: IMosaic) => {
        if (layout.order == null) {
            let m = 0;
            for (var l of Object.values(this.layouts))
                if (l.order != null)
                    m = Math.min(l.order, m);
            layout.order = m - 1;
        }
        let copyLayout = deepCopy(layout);
        this.cleanLayout(copyLayout);
        await userApi.saveLayout(copyLayout.title, copyLayout);
    }

    private cleanLayout = (layout: IMosaic): void => {
        return this.cleanLayoutRecursivly(layout.node);
    }
    private cleanLayoutRecursivly = (node: IMosaicNode): void => {
        if (node.nodes) {
            for (let i: number = 0; i < node.nodes.length; i++) {
                this.cleanLayoutRecursivly(node.nodes[i]);
            }
            return;
        }

        delete node.data;
    }

    flushChanges = async () => {
        if (!this.modified)
            return;
        let layout = this.getLayout();
        await this.saveLayout(layout);
        this.setModified(false);
    }

    invalidateLayout = () => {
        if (this.setModified(true) && this.getAutoSave())
            setTimeout(async () => {
                if (!this.modified)
                    return;
                await this.flushChanges();
            }, 5000);
    }

    deleteLayout = async (name: string = null) => {
        if (Object.keys(this.layouts).length <= 1)
            return false;
        if (!name)
            name = this.currentLayout.title;
        let wasModified = false;
        if (this.currentLayout.title == name)
            wasModified = this.setModified(false);
        let error: string = null;
        await userApi.removeLayout(name).catch(err => error = err);
        if (error) {
            if (wasModified)
                this.invalidateLayout();
            return false;
        }

        delete this.layouts[name];
        this.layoutsColelctionEvent.next(this.getLayoutSortedNames());
        if (this.currentLayout.title != name)
            return true;

        this.currentLayout = this.layouts[Object.keys(this.layouts)[0]];
        this.saveLayoutSelection();
        this.emitLayoutChange();
        return true;
    }

    public setEditingLayoutTitle = (title: string): boolean => {
        if (this.checkLayoutTitle(title))
            return false;
        this.editingLayout.title = title;
        return true;
    }
    public getLayoutByName = (name: string) => this.layouts[name];

    public getLayout = (): IMosaic => {
        let layout = this.editingLayout || this.currentLayout;
        //this.cleanLayout(layout);
        return layout;
    }
    private getLayoutSortedNames(): string[] {
        return Object.values(this.layouts).sort((a, b) => a.order - b.order).map(s => s.title);
    }
    public move = async (indexFrom: number, indexTo: number) => {
        let layoutList = Object.values(this.layouts).sort((a, b) => a.order - b.order);
        for (let i = 0; i < layoutList.length; i++)
            layoutList[i].order = (i * 2) + 1;
        if (indexFrom > indexTo)
            layoutList[indexFrom].order = layoutList[indexTo].order - 1;
        else
            layoutList[indexFrom].order = layoutList[indexTo].order + 1;
        layoutList = layoutList.sort((a, b) => a.order - b.order);
        for (let i = 0; i < layoutList.length; i++)
            layoutList[i].order = i;
        const sls = this.getLayoutSortedNames();
        this.layoutsColelctionEvent.next(sls);
        await userApi.saveLayoutSort(sls);
    }

    public getLayoutTitle = (): string => this.currentLayout?.title;

    public checkLayoutTitle = (title: string): boolean => !!this.layouts[title];

    public selectLayout = async (name: string, preEdit: boolean = false) => {
        if (this.modified)
            await this.flushChanges();
        const tids = new Set<string>();
        const newLayout = this.layouts[name];
        this.collectTids(newLayout?.node, tids);
        for (const tid of Array.from(tids))
            if (hub.storedTickers.has(tid)) {
                tids.delete(tid);
                if (tids.size == 0) break;
            }
        if (tids.size > 0) {
            const ts = await userApi.getTickersInfo(Array.from(tids));
            ts.forEach(x => hub.cacheTickers(ts, true));
        }
        this.currentLayout = this.layouts[name];
        this.saveLayoutSelection();
        this.emitLayoutChange(preEdit);
    }
    private collectTids(mosaic: IMosaicNode, tids: Set<string>) {
        const tid = mosaic?.settings?.currencyPair;
        if (tid)
            tids.add(tid);
        if (mosaic?.nodes)
            for (const child of mosaic.nodes)
                this.collectTids(child, tids);
    }
    //private isSameStr(l1: IMosaicNode, l2: IMosaicNode) {
    //	if (!l1?.nodes && !l2.nodes)
    //		return true;
    //	if (!l1?.nodes && l2?.nodes || l1?.nodes && !l2?.nodes)
    //		return false;
    //	if (l1.nodes.length != l2.nodes.length)
    //		return false;
    //	for (let i = 0; i < l1.nodes.length; i++) {
    //		if (!this.isSameStr(l1.nodes[i], l2.nodes[i]))
    //			return false;
    //	}
    //	return true;
    //}

    setEditMode = async (layout: IMosaic) => {
        if (layout)
            layout = deepCopy(layout);
        this.editingLayout = layout;
        this.emitLayoutChange();
    }


    public applyLayout = async () => {
        if (this.editingLayout?.title == null)
            return false;
        await this.saveLayout(this.editingLayout);
        this.editingLayout = deepCopy(this.editingLayout);
        this.cleanLayout(this.editingLayout);
        const newLayout = this.layouts[this.editingLayout.title] == null;
        this.layouts[this.editingLayout.title] = this.editingLayout;
        //if (this.currentLayout?.title == this.editingLayout.title)
        //  this.currentLayout = this.editingLayout;
        if (newLayout)
            this.layoutsColelctionEvent.next(this.getLayoutSortedNames());
        this.currentLayout = this.editingLayout;
        this.saveLayoutSelection();
        await this.setEditMode(null);
        return true;
    }
    public rollbackLayout = async () => {
        await this.setEditMode(null);
    }


    private emitLayoutChange = (preEdit: boolean = false) => this.layoutChangeEvent.next({ layout: this.getLayout(), isEdit: this.editingLayout != null || preEdit });

    public setAutoSave = (state: boolean) => {
        this.localStorage.saveObject(LayoutService.autoSaveKey, state);
        if (this.getAutoSave() && this.modified)
            this.flushChanges();
    }

    public getAutoSave = (): boolean => this.localStorage.getObject<boolean>(LayoutService.autoSaveKey);

    @Retryable({
        maxAttempts: 10,
        backOffPolicy: BackOffPolicy.ExponentialBackOffPolicy,
        backOff: 1000,
        exponentialOption: { maxInterval: 4000, multiplier: 2, backoffStrategy: ExponentialBackoffStrategy.EqualJitter }
    })
    static async ExponentialBackOffWithJitterRetry(func: () => {}) {
        await func();
    }

}


export interface TimeFrameToSelect {
    tvid: number;
    tf: TimeFrame;
}
